import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    parentEmail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const user = await register(
        formData.email,
        formData.password,
        formData.name,
        formData.role,
        formData.parentEmail
      );
      toast.success('Registration successful!');
      
      // Redirect based on role
      if (user.role === 'teacher') {
        navigate('/teacher/dashboard');
      } else if (user.role === 'student') {
        navigate('/student/dashboard');
      } else if (user.role === 'parent') {
        navigate('/parent/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6">
            <Code className="w-8 h-8 text-violet-600" />
            <span className="text-2xl font-bold text-slate-900">CodeITT</span>
          </Link>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Get Started</h1>
          <p className="text-slate-600">Create your account and start learning</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8" data-testid="register-form">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                data-testid="register-name-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="mt-1"
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="register-email-input"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="mt-1"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="register-password-input"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                className="mt-1"
                placeholder="Create a strong password"
              />
            </div>
            <div>
              <Label htmlFor="role">I am a...</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})} required>
                <SelectTrigger className="mt-1" data-testid="register-role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.role === 'student' && (
              <div>
                <Label htmlFor="parentEmail">Parent's Email (Optional)</Label>
                <Input
                  id="parentEmail"
                  type="email"
                  data-testid="register-parent-email-input"
                  value={formData.parentEmail}
                  onChange={(e) => setFormData({...formData, parentEmail: e.target.value})}
                  className="mt-1"
                  placeholder="parent@email.com"
                />
                <p className="text-xs text-slate-500 mt-1">Link to your parent's account for progress tracking</p>
              </div>
            )}
            <Button 
              type="submit" 
              className="w-full bg-violet-600 hover:bg-violet-700 h-11 rounded-full font-medium text-white"
              disabled={isSubmitting}
              data-testid="register-submit-btn"
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-violet-600 hover:text-violet-700 font-medium" data-testid="register-login-link">
                Login here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;